/**
 * Product / bulk-order components split out of global.js so they are not
 * shipped on pages that never use them (homepage, collections, cart, blog).
 *
 * Loaded by: main-product, featured-product, related-products,
 * bulk-quick-order-list, and any section rendering product cards with
 * quick_add == 'bulk'. Includes customElements.get() guards because more
 * than one of those can appear on the same page.
 */

// IIFE-scoped so that including this file from two sections on the same
// page cannot redeclare its top-level classes. BulkAdd is re-exported
// because quick-add-bulk.js and quick-order-list.js subclass it.
(function () {
  class BulkModal extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      const handleIntersection = (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        if (this.innerHTML.trim() === '') {
          const productUrl = this.dataset.url.split('?')[0];
          fetch(`${productUrl}?section_id=bulk-quick-order-list`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
              this.innerHTML = sourceQty.innerHTML;
            })
            .catch((e) => {
              console.error(e);
            });
        }
      };

      new IntersectionObserver(handleIntersection.bind(this)).observe(
        document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
      );
    }
  }

  if (!customElements.get('bulk-modal')) customElements.define('bulk-modal', BulkModal);

  class VariantSelects extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.addEventListener('change', (event) => {
        const target = this.getInputForEventTarget(event.target);
        this.updateSelectionMetadata(event);

        this.dispatchProductSelectEvent();

        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target,
            selectedOptionValues: this.selectedOptionValues,
          },
        });
      });
    }

    getAllSelectedOptions() {
      const options = [];
      this.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
        const checked = group.querySelector('input:checked') || group.querySelector('select option[selected]');
        if (checked) {
          options.push({ name: checked.dataset.optionName || '', value: checked.value });
        }
      });
      return options;
    }

    dispatchProductSelectEvent() {
      const { ProductSelectEvent } = window.StandardEvents || {};
      if (!ProductSelectEvent) return;

      const deferred = ProductSelectEvent.createPromise();
      this.pendingSelectPromise = deferred;

      this.dispatchEvent(
        new ProductSelectEvent({
          product: {
            id: this.dataset.productId,
            title: this.dataset.productTitle,
            handle: this.dataset.productHandle,
          },
          selectedOptions: this.getAllSelectedOptions(),
          promise: deferred.promise,
        })
      );
    }

    takePendingSelectPromise() {
      const deferred = this.pendingSelectPromise;
      this.pendingSelectPromise = null;
      return deferred;
    }

    resolvePendingSelectPromise(variant, sourceVariantSelects = this) {
      const deferred = this.takePendingSelectPromise();
      if (!deferred) return;

      if (variant) {
        deferred.resolve({
          variant: {
            id: variant.id,
            title: variant.title,
            availableForSale: variant.available,
            price: {
              amount: sourceVariantSelects?.dataset.selectedPriceAmount,
              currencyCode: sourceVariantSelects?.dataset.currencyCode,
            },
            selectedOptions: this.getAllSelectedOptions(),
          },
        });
      } else {
        deferred.resolve({ variant: null });
      }
    }

    rejectPendingSelectPromise(error) {
      this.takePendingSelectPromise()?.reject(error);
    }

    updateSelectionMetadata({ target }) {
      const { value, tagName } = target;

      if (tagName === 'SELECT' && target.selectedOptions.length) {
        Array.from(target.options)
          .find((option) => option.getAttribute('selected'))
          .removeAttribute('selected');
        target.selectedOptions[0].setAttribute('selected', 'selected');

        const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
        const selectedDropdownSwatchValue = target
          .closest('.product-form__input')
          .querySelector('[data-selected-value] > .swatch');
        if (!selectedDropdownSwatchValue) return;
        if (swatchValue) {
          selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
          selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
        } else {
          selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
          selectedDropdownSwatchValue.classList.add('swatch--unavailable');
        }

        selectedDropdownSwatchValue.style.setProperty(
          '--swatch-focal-point',
          target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
        );
      } else if (tagName === 'INPUT' && target.type === 'radio') {
        const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
        if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
      }
    }

    getInputForEventTarget(target) {
      return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
    }

    get selectedOptionValues() {
      return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
        ({ dataset }) => dataset.optionValueId
      );
    }
  }

  if (!customElements.get('variant-selects')) customElements.define('variant-selects', VariantSelects);

  class ProductRecommendations extends HTMLElement {
    observer = undefined;

    constructor() {
      super();
    }

    connectedCallback() {
      this.initializeRecommendations(this.dataset.productId);
    }

    initializeRecommendations(productId) {
      this.observer?.unobserve(this);
      this.observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);
          this.loadRecommendations(productId);
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }

    loadRecommendations(productId) {
      fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('product-recommendations');

          if (recommendations?.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }

          if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
            this.remove();
          }

          if (html.querySelector('.grid__item')) {
            this.classList.add('product-recommendations--loaded');
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  class BulkAdd extends HTMLElement {
    static ASYNC_REQUEST_DELAY = 250;

    constructor() {
      super();
      this.queue = [];
      this.setRequestStarted(false);
      this.ids = [];
    }

    startQueue(id, quantity) {
      this.queue.push({ id, quantity });

      const interval = setInterval(() => {
        if (this.queue.length > 0) {
          if (!this.requestStarted) {
            this.sendRequest(this.queue);
          }
        } else {
          clearInterval(interval);
        }
      }, BulkAdd.ASYNC_REQUEST_DELAY);
    }

    sendRequest(queue) {
      this.setRequestStarted(true);
      const items = {};

      queue.forEach((queueItem) => {
        items[parseInt(queueItem.id)] = queueItem.quantity;
      });
      this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

      this.updateMultipleQty(items);
    }

    setRequestStarted(requestStarted) {
      this._requestStarted = requestStarted;
    }

    get requestStarted() {
      return this._requestStarted;
    }

    getCartQuantityForLine(id) {
      const input = this.querySelector(`#Quantity-${id}`);
      return parseInt(input?.dataset.cartQuantity || input?.getAttribute('value') || '0', 10) || 0;
    }

    startCartLinesUpdate(items) {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!CartLinesUpdateEvent) return;

      const linesByAction = Object.entries(items).reduce((groups, [variantId, quantity]) => {
        const nextQuantity = parseInt(quantity, 10);
        const currentQuantity = this.getCartQuantityForLine(variantId);

        if (Number.isNaN(nextQuantity) || currentQuantity === nextQuantity) return groups;

        const action = currentQuantity === 0 ? 'add' : nextQuantity === 0 ? 'remove' : 'update';
        let line;
        if (action === 'add') {
          line = { merchandiseId: variantId, quantity: nextQuantity };
        } else {
          const lineKey = this.querySelector(`[data-quantity-variant-id="${variantId}"]`)?.dataset.quantityLineKey;
          // No AJAX line key on the row — likely cached HTML rendered before this
          // attribute landed. Skip rather than emit an event with id: ''.
          if (!lineKey) return groups;
          line = { id: lineKey, quantity: nextQuantity };
        }

        if (!groups[action]) groups[action] = [];
        groups[action].push(line);
        return groups;
      }, {});

      const deferreds = Object.entries(linesByAction).map(([action, lines]) => {
        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action,
            context: 'product',
            lines,
            promise: deferred.promise,
          })
        );
        return deferred;
      });

      return {
        resolve: (parsedState) => {
          const payload = { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedState) };
          deferreds.forEach((deferred) => deferred.resolve(payload));
        },
        reject: (error) => {
          deferreds.forEach((deferred) => deferred.reject(error));
        },
      };
    }

    dispatchCartErrorEvent(message, code) {
      const { CartErrorEvent } = window.StandardEvents || {};
      if (!CartErrorEvent) return;

      this.dispatchEvent(new CartErrorEvent({ error: message, code }));
    }

    resetQuantityInput(id) {
      const input = this.querySelector(`#Quantity-${id}`);
      input.value = input.getAttribute('value');
      this.isEnterPressed = false;
    }

    setValidity(event, index, message) {
      event.target.setCustomValidity(message);
      event.target.reportValidity();
      this.resetQuantityInput(index);
      event.target.select();
    }

    validateQuantity(event) {
      const inputValue = parseInt(event.target.value);
      const index = event.target.dataset.index;

      if (inputValue < event.target.dataset.min) {
        this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
      } else if (inputValue > parseInt(event.target.max)) {
        this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
      } else if (inputValue % parseInt(event.target.step) != 0) {
        this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
      } else {
        event.target.setCustomValidity('');
        event.target.reportValidity();
        event.target.setAttribute('value', inputValue);
        this.startQueue(index, inputValue);
      }
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }
  }

  if (!customElements.get('bulk-add')) {
    customElements.define('bulk-add', BulkAdd);
  }

  window.BulkAdd = BulkAdd;
})();
